import {
  SB_DEFAULT_PLACEMENT_ENUMS,
  SB_MODIFIER_ORDER_TIERS,
  SB_PLACEMENT_ENUM_MATCHERS,
  SB_PLACEMENT_MODIFIER_LIMITS,
  SB_PLACEMENT_ROW_NAMES,
  sbModifierMin,
} from "../../constants/placements.constants"
import { ISbPlacementRowForTable } from "../../types/sbPlacement.types"
import {
  IAudienceBidAdjustment,
  IBidAdjustmentByPlacement,
  ISbBidAdjustmentItem,
  ISbCampaignBidding,
  ISbPlacementResult,
  ISbPlacementRow,
} from "../../types/sbPlacementApi.types"
import {
  canRaise,
  clamp,
  deflateBid,
  gateRaise,
  isEnabledTarget,
  weightedAdjPct,
} from "../../../../lib/bidCore"

// Amazon SB minimum bid: floor of the FINAL bid (twin: audit SB_AMAZON_MIN_BID).
export const SB_AMAZON_MIN_BID = 0.1

function clampModifier(placement: string, value: number): number {
  return Math.min(Math.max(value, sbModifierMin(placement)), SB_PLACEMENT_MODIFIER_LIMITS.MAX)
}

export const SB_RAISE_CAP_PCT = 10
export const SB_RAISE_CAP_MAX_ORDERS = 3

export function capSbBidRaise(
  bid: number | null,
  currentBid: number | null | undefined,
  orders: number | null | undefined
): number | null {
  const current = Number(currentBid)
  if (bid === null || !Number.isFinite(bid) || !(current > 0) || bid <= current) {
    return bid
  }
  if (Number(orders) > SB_RAISE_CAP_MAX_ORDERS) {
    return bid
  }
  return Math.min(bid, roundBid(current * (1 + SB_RAISE_CAP_PCT / 100)) as number)
}

export interface IFinalBidTarget {
  limitedCPC: number | null
  hold?: boolean
  excluded?: boolean
  bid?: number | null
  orders?: number | null
  clicks?: number | null
}

export interface IBaseBidTarget extends IFinalBidTarget {
  targetId: string | number
  targetStatus?: unknown
  bid: number | null
  spend?: number | null
}

// The placement base = spend-weighted UNDEFLATED bid (Limited CPC after the raise cap and the
// Amazon min/max) of the ENABLED, non-excluded targets. Modifiers are sized against this
// undeflated base so the pushed (deflated) bids can never feed back into the modifiers — that
// feedback was the pre-2026-10-06 spiral to the min bid.
export function computeBaseBid(
  targets: IBaseBidTarget[],
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number | null {
  return weightedAverageBidOverUniqueTargets(baseBidTargets(targets), (target) =>
    selectFinalBidToApply(target, context, minBid, maxBid)
  )
}

export function countBaseBidTargets(targets: IBaseBidTarget[]): number {
  return new Set(baseBidTargets(targets).map((target) => String(target.targetId))).size
}

export function computeLiveWeightedBid(targets: IBaseBidTarget[]): number | null {
  return weightedAverageBidOverUniqueTargets(enabledTargets(targets), (target) =>
    normalizeMetric(target.bid)
  )
}

const LIVE_SIZING_MODIFIER_PCT = 100
const LIVE_SIZING_BASE_RATIO = 1.5

export function isBaseAboveLiveSizing(
  baseBid: number | null,
  liveWeightedBid: number | null,
  liveModifierPct: number
): boolean {
  if (baseBid === null || liveWeightedBid === null || liveWeightedBid <= 0) {
    return false
  }
  return (
    liveModifierPct >= LIVE_SIZING_MODIFIER_PCT &&
    baseBid > liveWeightedBid * LIVE_SIZING_BASE_RATIO
  )
}

// A7 (multi-target): the shared placement modifier multiplies EVERY target, so a thin-data
// placement must hold for the target whose bid rises the most: max(final / live) over the
// enabled, non-excluded targets with a live bid and a final bid (null = no such target).
export function computeMaxTargetRaiseRatio(
  targets: IBaseBidTarget[],
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number | null {
  let maxRatio: number | null = null
  const seen = new Set<string>()
  // EVERY enabled target is multiplied by the shared modifiers — a target that gets no bid update
  // (excluded / no valid Limited CPC) keeps its live bid, i.e. ratio 1 (NEW-SB-UNCHANGED-TARGET).
  for (const target of enabledTargets(targets)) {
    const id = String(target.targetId)
    if (seen.has(id)) continue
    seen.add(id)
    const live = positiveOrNull(target.bid)
    if (live === null) continue
    const final = computeFinalBid(target, context, minBid, maxBid)
    const ratio = final === null ? 1 : final / live
    if (maxRatio === null || ratio > maxRatio) maxRatio = ratio
  }
  return maxRatio
}

export function computeCurrentBid(targets: IBaseBidTarget[]): number | null {
  return averageOverUniqueTargets(enabledTargets(targets), (target) => target.bid)
}

function enabledTargets(targets: IBaseBidTarget[]): IBaseBidTarget[] {
  return targets.filter((target) => isEnabledTarget(target.targetStatus))
}

function baseBidTargets(targets: IBaseBidTarget[]): IBaseBidTarget[] {
  return enabledTargets(targets).filter((target) => !target.excluded)
}

function averageOverUniqueTargets(
  targets: IBaseBidTarget[],
  selectValue: (target: IBaseBidTarget) => number | null
): number | null {
  const valueByTargetId = new Map<string, number | null>()
  for (const target of targets) {
    const targetId = String(target.targetId)
    if (!valueByTargetId.has(targetId)) {
      valueByTargetId.set(targetId, selectValue(target))
    }
  }

  if (valueByTargetId.size === 0) {
    return null
  }

  let totalValue = 0
  valueByTargetId.forEach((value) => {
    totalValue += value ?? 0
  })

  return totalValue / valueByTargetId.size
}

function weightedAverageBidOverUniqueTargets(
  targets: IBaseBidTarget[],
  selectBid: (target: IBaseBidTarget) => number | null
): number | null {
  const valueByTargetId = new Map<string, { bid: number | null; spend: number }>()

  for (const target of targets) {
    const targetId = String(target.targetId)
    const bid = selectBid(target)
    const spend = normalizeMetric(target.spend) ?? 0
    const existing = valueByTargetId.get(targetId)

    if (!existing) {
      valueByTargetId.set(targetId, { bid, spend })
      continue
    }

    valueByTargetId.set(targetId, {
      bid: existing.bid ?? bid,
      spend: existing.spend + spend,
    })
  }

  const values = [...valueByTargetId.values()].filter((target) => target.bid !== null)
  if (values.length === 0) {
    return null
  }

  const totalSpend = values.reduce((sum, target) => sum + target.spend, 0)
  if (totalSpend <= 0) {
    return null
  }

  return values.reduce((sum, target) => sum + (target.bid ?? 0) * target.spend, 0) / totalSpend
}

function normalizeMetric(value: number | null | undefined): number | null {
  return Number.isFinite(value) ? Number(value) : null
}

function selectFinalBidToApply(
  target: IBaseBidTarget,
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number | null {
  // [NEW-BASE-03] Same semantics as computeFinalBid but UNDEFLATED, no live-bid fallback: a target
  // Apply cannot push a final bid for (no valid Limited CPC) contributes nothing to the base.
  return computeFinalBidBeforePlacements(target, context, minBid, maxBid)
}

function positiveOrNull(value: number | null | undefined): number | null {
  const normalized = normalizeMetric(value)
  return normalized !== null && normalized > 0 ? normalized : null
}

export interface ISbDeflationContext {
  pending: ISbBidAdjustmentItem | null
  bidding: ISbCampaignBidding
  placements: ISbPlacementRow[]
}

export function resolveDeflationContext(
  campaignId: string,
  pendingAdjustments: Record<string, ISbBidAdjustmentItem>,
  placementData: ISbPlacementResult[]
): ISbDeflationContext {
  const placement = placementData.find((item) => String(item.campaignId) === String(campaignId))

  return {
    pending: pendingAdjustments[campaignId] ?? null,
    bidding: placement?.bidding ?? {},
    placements: placement?.placements ?? [],
  }
}

// The bid Apply PUSHES (Anton 2026-10-17, restored): the target's Limited CPC is an EFFECTIVE CPC
// (it is computed from the CPC actually paid, placement multipliers included), and Amazon
// multiplies the pushed bid by the placement multipliers again — so the pushed bid is the
// Limited CPC ÷ (1 + W), W = the campaign's spend-weighted placement modifier (pending over live).
// Then: raise cap, Amazon min/max (maxBid <= 0 = unset), A7 raise gate, A7 placement coordination.
export function computeFinalBid(
  target: IFinalBidTarget,
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number | null {
  return finalBid(target, context, minBid, maxBid, true)
}

// Same pipeline WITHOUT the ÷ (1 + W) step: the placement base (computeBaseBid) only.
export function computeFinalBidBeforePlacements(
  target: IFinalBidTarget,
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number | null {
  return finalBid(target, context, minBid, maxBid, false)
}

function finalBid(
  target: IFinalBidTarget,
  context: ISbDeflationContext,
  minBid: number,
  maxBid: number | undefined,
  deflate: boolean
): number | null {
  if (target.excluded) {
    return null
  }
  if (target.hold) {
    return positiveOrNull(target.limitedCPC)
  }
  const limited = positiveOrNull(target.limitedCPC)
  const desired =
    deflate && limited !== null
      ? deflateBid(limited, computeSpendWeightedModifier(context))
      : limited

  const capped = capSbBidRaise(roundBid(desired), target.bid, target.orders)
  if (capped === null) {
    return null
  }
  // A7 runs AFTER the Amazon min/max clamp so the min can never re-raise a thin-data bid.
  const bounded = clamp(capped, minBid, positiveOrNull(maxBid) ?? undefined)
  return capByGatedPlacements(gateRaise(bounded, target.bid, target) as number, target.bid, context)
}

// A7 placement coordination: a target raise multiplies every placement's effective bid. For a
// placement with < 3 orders or < 10 clicks the modifier can only absorb it down to its Amazon
// minimum, so the target bid may rise at most to current × (1 + live mod) / (1 + min mod).
function capByGatedPlacements(
  bid: number,
  currentBid: number | null | undefined,
  context: ISbDeflationContext
): number {
  const current = positiveOrNull(currentBid)
  if (current === null || !(bid > current)) {
    return bid
  }
  let ceiling = Infinity
  for (const row of context.placements) {
    if (canRaise(row)) {
      continue
    }
    const isAudience = row.placement === SB_PLACEMENT_ROW_NAMES.AUDIENCE
    if (isAudience && !hasConfiguredAudiences(context.bidding)) {
      continue
    }
    const liveModifier = resolvePlacementModifier(context.bidding, row.placement) ?? 0
    const factor = (1 + liveModifier / 100) / (1 + sbModifierMin(row.placement) / 100)
    ceiling = Math.min(ceiling, Math.floor(current * factor * 100 + 1e-9) / 100)
  }
  return Math.min(bid, Math.max(current, ceiling))
}

// Spend-weighted modifier W of the campaign (pending over live): the pushed bid = Limited CPC ÷ (1 + W).
export function computeSpendWeightedModifier(context: ISbDeflationContext): number {
  const placementNames = Object.values(SB_PLACEMENT_ROW_NAMES)

  return weightedAdjPct({
    spendByPlacement: Object.fromEntries(
      context.placements.map((row) => [row.placement, row.spend])
    ),
    adjPctByPlacement: Object.fromEntries(
      placementNames.map((name) => [name, resolveEffectiveModifier(context, name)])
    ),
  })
}

function resolveEffectiveModifier(context: ISbDeflationContext, placementRowName: string): number {
  return (
    findPendingModifier(context.pending, placementRowName) ??
    resolvePlacementModifier(context.bidding, placementRowName) ??
    0
  )
}

function findPendingModifier(
  pending: ISbBidAdjustmentItem | null,
  placementRowName: string
): number | null {
  if (!pending) {
    return null
  }
  if (placementRowName === SB_PLACEMENT_ROW_NAMES.AUDIENCE) {
    return normalizeMetric(pending.audiences[0]?.percentage)
  }

  return (
    findPlacementAdjustment({ bidAdjustmentsByPlacement: pending.placements }, placementRowName)
      ?.percentage ?? null
  )
}

export function computePlacementBid(row: ISbPlacementRow): number | null {
  if (row.acos <= 0) {
    return null
  }
  return (row.cpc * row.targetAcos) / row.acos
}

export function roundBid(bid: number | null): number | null {
  return bid === null ? null : Number(bid.toFixed(2))
}

export function computeBidFromModifier(
  baseBid: number | null,
  modifier: number | null
): number | null {
  if (baseBid === null || modifier === null) {
    return null
  }
  return baseBid * (1 + modifier / 100)
}

// Caps the auto-SUGGESTED modifier by the row's own order count so tiny-sample
// placements cannot push a 900% modifier that deflates base bids.
// Manual editing in the UI is unaffected (still bounded by min/900 only).
export function maxSuggestedModifierForOrders(orders: number | null | undefined): number {
  const normalizedOrders =
    Number.isFinite(orders) && (orders as number) > 0 ? (orders as number) : 0

  for (const tier of SB_MODIFIER_ORDER_TIERS) {
    if (normalizedOrders <= tier.maxOrders) {
      return tier.maxModifier
    }
  }
  return SB_PLACEMENT_MODIFIER_LIMITS.MAX
}

function computeUncappedPlacementModifier(
  placement: string,
  placementBid: number | null,
  baseBid: number | null
): number | null {
  if (placementBid === null || baseBid === null || baseBid <= 0) {
    return null
  }
  return clampModifier(placement, Math.round((placementBid / baseBid - 1) * 100))
}

function computePlacementModifier(
  placement: string,
  placementBid: number | null,
  baseBid: number | null,
  orders: number | null | undefined
): number | null {
  const uncapped = computeUncappedPlacementModifier(placement, placementBid, baseBid)
  if (uncapped === null) {
    return null
  }
  return Math.min(uncapped, maxSuggestedModifierForOrders(orders))
}

function computeUncappedAudienceModifier(
  audienceBid: number | null,
  maxPlacementBid: number | null
): number | null {
  if (audienceBid === null || maxPlacementBid === null || maxPlacementBid <= 0) {
    return null
  }
  return clampModifier(
    SB_PLACEMENT_ROW_NAMES.AUDIENCE,
    Math.round((audienceBid / maxPlacementBid - 1) * 100)
  )
}

function computeAudienceModifier(
  audienceBid: number | null,
  maxPlacementBid: number | null,
  orders: number | null | undefined
): number | null {
  const uncapped = computeUncappedAudienceModifier(audienceBid, maxPlacementBid)
  if (uncapped === null) {
    return null
  }
  return Math.min(uncapped, maxSuggestedModifierForOrders(orders))
}

function maxDefinedBid(first: number | null, second: number | null): number | null {
  if (first === null) {
    return second
  }
  if (second === null) {
    return first
  }
  return Math.max(first, second)
}

export function findPlacementAdjustment(
  bidding: ISbCampaignBidding,
  placementRowName: string
): IBidAdjustmentByPlacement | null {
  const matchers = SB_PLACEMENT_ENUM_MATCHERS[placementRowName] ?? []
  const adjustments = bidding.bidAdjustmentsByPlacement ?? []

  return (
    adjustments.find((adjustment) =>
      matchers.some((matcher) => adjustment.placement.toUpperCase().includes(matcher))
    ) ?? null
  )
}

export function resolvePlacementModifier(
  bidding: ISbCampaignBidding,
  placementRowName: string
): number | null {
  if (placementRowName === SB_PLACEMENT_ROW_NAMES.AUDIENCE) {
    return resolveAudienceModifier(bidding)
  }

  return findPlacementAdjustment(bidding, placementRowName)?.percentage ?? null
}

export function resolveAudienceModifier(bidding: ISbCampaignBidding): number | null {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  return cohorts.length ? cohorts[0].percentage : null
}

export function resolveAudienceId(bidding: ISbCampaignBidding): string | null {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  const segments = cohorts[0]?.audienceSegments ?? []
  return segments[0]?.audienceId ?? null
}

export function hasAudienceChange(
  bidding: ISbCampaignBidding,
  selectedAudienceId: string | null
): boolean {
  if (selectedAudienceId === null) {
    return false
  }
  return selectedAudienceId !== resolveAudienceId(bidding)
}

export function hasConfiguredAudiences(bidding: ISbCampaignBidding): boolean {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  return cohorts.some((cohort) => (cohort.audienceSegments ?? []).length > 0)
}

export function resolvePlacementEnum(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string {
  return (
    findPlacementAdjustment(bidding, placementRowName)?.placement ??
    SB_DEFAULT_PLACEMENT_ENUMS[placementRowName] ??
    placementRowName
  )
}

export function findAllPlacementEnums(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string[] {
  const matchers = SB_PLACEMENT_ENUM_MATCHERS[placementRowName] ?? []
  const adjustments = bidding.bidAdjustmentsByPlacement ?? []

  return adjustments
    .map((adjustment) => adjustment.placement)
    .filter((placement) => matchers.some((matcher) => placement.toUpperCase().includes(matcher)))
}

export function resolvePlacementEnumsForWrite(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string[] {
  const existing = findAllPlacementEnums(bidding, placementRowName)
  if (existing.length) {
    return existing
  }

  return [SB_DEFAULT_PLACEMENT_ENUMS[placementRowName] ?? placementRowName]
}

// A7: a placement with < 3 orders or < 10 clicks may not raise its effective bid
// (base × (1 + modifier)) over the live one (live base × (1 + live modifier)).
function gatePlacementModifier(
  placementRow: ISbPlacementRow,
  modifier: number | null,
  bidding: ISbCampaignBidding,
  baseBid: number | null,
  liveBaseBid: number | null,
  maxTargetRaiseRatio: number | null = null
): number | null {
  if (canRaise(placementRow) || baseBid === null || !(baseBid > 0)) {
    return modifier
  }
  const name = placementRow.placement
  const liveModifier = resolvePlacementModifier(bidding, name) ?? 0
  const finalModifier = modifier ?? liveModifier
  let maxModifier = Infinity
  if (liveBaseBid !== null && liveBaseBid > 0) {
    const liveEffective = liveBaseBid * (1 + liveModifier / 100)
    maxModifier = Math.floor((liveEffective / baseBid - 1) * 100 + 1e-9)
  }
  // Per target: final_t × (1 + m) <= live_t × (1 + live m) for EVERY target, i.e. bounded by the
  // most restrictive (largest) final/live ratio — a weighted base can hide one target's raise.
  if (maxTargetRaiseRatio !== null && maxTargetRaiseRatio > 0) {
    maxModifier = Math.min(
      maxModifier,
      Math.floor(((1 + liveModifier / 100) / maxTargetRaiseRatio - 1) * 100 + 1e-9)
    )
  }
  if (!Number.isFinite(maxModifier)) {
    return modifier
  }
  return finalModifier <= maxModifier ? modifier : Math.max(sbModifierMin(name), maxModifier)
}

// Largest final/live target ratio (per-target when known, else the base ratio, else 1).
function raiseRatioOf(
  baseBid: number | null,
  liveBaseBid: number | null,
  maxTargetRaiseRatio: number | null
): number {
  if (maxTargetRaiseRatio !== null && maxTargetRaiseRatio > 0) return maxTargetRaiseRatio
  if (baseBid !== null && baseBid > 0 && liveBaseBid !== null && liveBaseBid > 0)
    return baseBid / liveBaseBid
  return 1
}

// A7 (NEW-SB-AUDIENCE-PLACEMENT-LEAK): the Audience adjustment stacks on the placement
// adjustment, so a thin-data Audience row's effective bid is target × (1 + p) × (1 + a) for every
// placement p. Keep it within live: lower the audience modifier first; when it would fall below
// its floor, hold the audience at the floor and lower the placement modifiers instead.
function coordinateThinAudience(
  rows: ISbPlacementRow[],
  bidding: ISbCampaignBidding,
  modifierByPlacement: Map<string, number | null>,
  cappedDisplayedBidByPlacement: Map<string, number | null>,
  baseBid: number | null,
  ratio: number
): void {
  const audienceName = SB_PLACEMENT_ROW_NAMES.AUDIENCE
  const audienceRow = rows.find((row) => row.placement === audienceName)
  if (!audienceRow || canRaise(audienceRow) || !hasConfiguredAudiences(bidding)) return
  const liveAudience = resolvePlacementModifier(bidding, audienceName) ?? 0
  const audienceMin = sbModifierMin(audienceName)
  const mains = rows.filter((row) => row.placement !== audienceName)
  const finalOf = (name: string) =>
    modifierByPlacement.get(name) ?? resolvePlacementModifier(bidding, name) ?? 0
  const liveOf = (name: string) => resolvePlacementModifier(bidding, name) ?? 0
  let audienceCap = Infinity
  for (const row of mains) {
    const allowed =
      ((1 + liveOf(row.placement) / 100) * (1 + liveAudience / 100)) /
      (ratio * (1 + finalOf(row.placement) / 100))
    audienceCap = Math.min(audienceCap, Math.floor((allowed - 1) * 100 + 1e-9))
  }
  const suggested = modifierByPlacement.get(audienceName) ?? liveAudience
  if (!Number.isFinite(audienceCap) || suggested <= audienceCap) return
  const audience = Math.max(audienceMin, audienceCap)
  modifierByPlacement.set(audienceName, audience)
  cappedDisplayedBidByPlacement.set(
    audienceName,
    roundBid(computeBidFromModifier(baseBid, audience))
  )
  if (audienceCap >= audienceMin) return
  for (const row of mains) {
    const name = row.placement
    const maxPlacement = Math.floor(
      (((1 + liveOf(name) / 100) * (1 + liveAudience / 100)) / (ratio * (1 + audienceMin / 100)) -
        1) *
        100 +
        1e-9
    )
    if (finalOf(name) > maxPlacement) {
      const lowered = Math.max(sbModifierMin(name), maxPlacement)
      modifierByPlacement.set(name, lowered)
      cappedDisplayedBidByPlacement.set(name, roundBid(computeBidFromModifier(baseBid, lowered)))
    }
  }
}

export function buildPlacementRowsForTable(
  placements: ISbPlacementRow[],
  bidding: ISbCampaignBidding,
  baseBid: number | null,
  currentBid: number | null,
  liveBaseBid: number | null = currentBid,
  maxTargetRaiseRatio: number | null = null,
  thinDataDefaults: ISbThinDataDefaults | null = null
): ISbPlacementRowForTable[] {
  const enriched = placements.map((placementRow) => ({
    placementRow,
    isAudience: placementRow.placement === SB_PLACEMENT_ROW_NAMES.AUDIENCE,
    baseBid,
    placementBid: computePlacementBid(placementRow),
  }))

  // Each non-audience placement row (Top of Search / Rest of Search / Detail Page / Home)
  // gets its own suggested modifier and displayed bid.
  const modifierByPlacement = new Map<string, number | null>()
  const cappedDisplayedBidByPlacement = new Map<string, number | null>()
  let maxPlacementBid: number | null = null

  for (const item of enriched) {
    if (item.isAudience) {
      continue
    }
    const name = item.placementRow.placement
    const uncapped = computeUncappedPlacementModifier(name, item.placementBid, item.baseBid)
    const modifier = computePlacementModifier(
      name,
      item.placementBid,
      item.baseBid,
      item.placementRow.orders
    )
    const capBinds = modifier !== null && uncapped !== null && modifier < uncapped
    const displayedBid = roundBid(
      capBinds
        ? computeBidFromModifier(baseBid, modifier)
        : resolveDisplayPlacementBid(
            item.placementBid,
            baseBid,
            modifier ?? resolvePlacementModifier(bidding, name)
          )
    )

    modifierByPlacement.set(name, modifier)
    if (capBinds) {
      cappedDisplayedBidByPlacement.set(name, displayedBid)
    }
    // STEP 2: Audience is measured against the highest CALCULATED placement bid
    // (CPC × targetACOS ÷ ACOS), never the rounded/order-capped displayed bid.
    maxPlacementBid = maxDefinedBid(maxPlacementBid, item.placementBid)
  }

  const audience = enriched.find((item) => item.isAudience)
  const audienceUncappedModifier = computeUncappedAudienceModifier(
    audience?.placementBid ?? null,
    maxPlacementBid
  )
  const audienceModifier = computeAudienceModifier(
    audience?.placementBid ?? null,
    maxPlacementBid,
    audience?.placementRow.orders
  )
  const audienceCapBinds =
    audienceModifier !== null &&
    audienceUncappedModifier !== null &&
    audienceModifier < audienceUncappedModifier

  if (audienceCapBinds) {
    const audienceCappedBid = roundBid(computeBidFromModifier(baseBid, audienceModifier))
    if (audienceCappedBid !== null) {
      cappedDisplayedBidByPlacement.set(SB_PLACEMENT_ROW_NAMES.AUDIENCE, audienceCappedBid)
    }
  }
  modifierByPlacement.set(SB_PLACEMENT_ROW_NAMES.AUDIENCE, audienceModifier)

  for (const { placementRow } of enriched) {
    const name = placementRow.placement
    const suggested = modifierByPlacement.get(name) ?? null
    const gated = gatePlacementModifier(
      placementRow,
      suggested,
      bidding,
      baseBid,
      liveBaseBid,
      maxTargetRaiseRatio
    )
    if (gated !== suggested) {
      modifierByPlacement.set(name, gated)
      cappedDisplayedBidByPlacement.set(name, roundBid(computeBidFromModifier(baseBid, gated)))
    }
  }

  coordinateThinAudience(
    enriched.map((item) => item.placementRow),
    bidding,
    modifierByPlacement,
    cappedDisplayedBidByPlacement,
    baseBid,
    raiseRatioOf(baseBid, liveBaseBid, maxTargetRaiseRatio)
  )

  // Anton 2026-10-18: a thin-data row (< 3 orders or < 10 clicks) may carry at most the default
  // placement multiplier (PPC settings campaign creation defaults); lower values stay lower.
  capThinRowsAtDefaults(
    enriched.map((item) => item.placementRow),
    bidding,
    modifierByPlacement,
    cappedDisplayedBidByPlacement,
    baseBid,
    thinDataDefaults
  )

  return enriched.map(({ placementRow, isAudience, baseBid, placementBid }) => {
    const modifier =
      modifierByPlacement.get(placementRow.placement) ??
      resolvePlacementModifier(bidding, placementRow.placement)
    const currentModifier = resolvePlacementModifier(bidding, placementRow.placement)

    const cappedDisplayedBid = cappedDisplayedBidByPlacement.get(placementRow.placement)

    return {
      ...placementRow,
      baseBid: roundBid(baseBid),
      placementBid:
        cappedDisplayedBid !== undefined
          ? cappedDisplayedBid
          : roundBid(resolveDisplayPlacementBid(placementBid, baseBid, modifier)),
      modifier,
      currentModifier,
      currentPlacementBid: roundBid(currentBid),
      isAudience,
      isModifierEditable: !isAudience || hasConfiguredAudiences(bidding),
    }
  })
}

/**
 * SB default multipliers for thin-data rows — rankattack.com PPC settings (Settings -> Other):
 * Top of Search = TOSPercentage, Audience = ADPercentage. Rest of Search / Detail page / Home page have no
 * SB setting in .com and stay at 0% (same as rankattack.net campaignCreationDefaults.sb otherThanTOS).
 * Fallbacks (settings not loaded / missing): TOS 50%, Audience 10%.
 */
export interface ISbThinDataDefaults {
  TOSPercentage?: number | null
  ADPercentage?: number | null
}

export const SB_THIN_DATA_MAX_MODIFIER_PCT: Record<string, number> = {
  [SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH]: 50,
  [SB_PLACEMENT_ROW_NAMES.AUDIENCE]: 10,
}
const SB_THIN_DATA_OTHER_MAX_PCT = 0

function settingPct(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null
}

export function sbThinDataMaxModifier(
  placementRowName: string,
  defaults: ISbThinDataDefaults | null = null
): number {
  if (placementRowName === SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH) {
    return settingPct(defaults?.TOSPercentage) ?? SB_THIN_DATA_MAX_MODIFIER_PCT[placementRowName]
  }
  if (placementRowName === SB_PLACEMENT_ROW_NAMES.AUDIENCE) {
    return settingPct(defaults?.ADPercentage) ?? SB_THIN_DATA_MAX_MODIFIER_PCT[placementRowName]
  }
  return SB_THIN_DATA_OTHER_MAX_PCT
}

function capThinRowsAtDefaults(
  rows: ISbPlacementRow[],
  bidding: ISbCampaignBidding,
  modifierByPlacement: Map<string, number | null>,
  cappedDisplayedBidByPlacement: Map<string, number | null>,
  baseBid: number | null,
  defaults: ISbThinDataDefaults | null
): void {
  for (const row of rows) {
    if (canRaise(row)) continue
    const name = row.placement
    if (name === SB_PLACEMENT_ROW_NAMES.AUDIENCE && !hasConfiguredAudiences(bidding)) continue
    const current = modifierByPlacement.get(name) ?? resolvePlacementModifier(bidding, name)
    if (current === null || current === undefined) continue
    const max = Math.max(sbModifierMin(name), sbThinDataMaxModifier(name, defaults))
    if (current > max) {
      modifierByPlacement.set(name, max)
      cappedDisplayedBidByPlacement.set(name, roundBid(computeBidFromModifier(baseBid, max)))
    }
  }
}

function resolveDisplayPlacementBid(
  placementBid: number | null,
  baseBid: number | null,
  modifier: number | null
): number | null {
  if (placementBid !== null) {
    return placementBid
  }
  return computeBidFromModifier(baseBid, modifier)
}

export function buildAudienceAdjustments(
  bidding: ISbCampaignBidding,
  percentage: number
): IAudienceBidAdjustment[] {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []

  return cohorts.flatMap((cohort) =>
    (cohort.audienceSegments ?? []).map((segment) => ({
      audienceId: segment.audienceId,
      percentage,
    }))
  )
}

export function buildBidAdjustmentItem(
  campaignId: string,
  rows: ISbPlacementRowForTable[],
  bidding: ISbCampaignBidding,
  selectedAudienceId: string | null = null
): ISbBidAdjustmentItem {
  const placementRows = rows.filter((row) => !row.isAudience && row.modifier !== null)
  const audienceRow = rows.find((row) => row.isAudience && row.modifier !== null)

  const placements = placementRows.flatMap((row) =>
    resolvePlacementEnumsForWrite(bidding, row.placement).map((placement) => ({
      placement,
      percentage: row.modifier as number,
    }))
  )
  // Keep Amazon placements without a row in the payload (e.g. rows with no modifier) at their current %.
  const written = new Set(placements.map((item) => item.placement))
  const untouched = placementRows.length
    ? (bidding.bidAdjustmentsByPlacement ?? [])
        .filter((item) => !written.has(item.placement))
        .map(({ placement, percentage }) => ({ placement, percentage }))
    : []

  return {
    campaignId,
    placements: [...placements, ...untouched],
    audiences: buildAudiencesForWrite(bidding, audienceRow, selectedAudienceId),
  }
}

function buildAudiencesForWrite(
  bidding: ISbCampaignBidding,
  audienceRow: ISbPlacementRowForTable | undefined,
  selectedAudienceId: string | null
): IAudienceBidAdjustment[] {
  if (selectedAudienceId) {
    return [{ audienceId: selectedAudienceId, percentage: audienceRow?.modifier ?? undefined }]
  }

  return audienceRow ? buildAudienceAdjustments(bidding, audienceRow.modifier as number) : []
}

export function buildAudienceSelectionAdjustment(
  campaignId: string,
  existing: ISbBidAdjustmentItem | undefined,
  audienceId: string
): ISbBidAdjustmentItem {
  return {
    campaignId,
    placements: existing?.placements ?? [],
    audiences: [{ audienceId }],
  }
}

export function hasModifierChanges(
  rows: ISbPlacementRowForTable[],
  bidding: ISbCampaignBidding
): boolean {
  return rows.some((row) => row.modifier !== resolvePlacementModifier(bidding, row.placement))
}
